import * as ResponsiveValue from './responsive-value';
import '@famly/mf_theme';
export const alignmentRelatedSystemProps = [
    'm',
    'margin',
    'mb',
    'marginBottom',
    'ml',
    'marginLeft',
    'mr',
    'marginRight',
    'mt',
    'marginTop',
    'mx',
    'marginX',
    'my',
    'marginY',
    'bottom',
    'left',
    'position',
    'right',
    'top',
    'alignSelf',
    'flex',
    'flexGrow',
    'flexShrink',
    'flexBasis',
    'order',
];
/**
 * System props that we want to support in Modern Famly.
 *
 * This list can be expanded over time.
 */
export const supportedSystemProps = [
    'cursor',
    'displayPrint',
    'display',
    'alignContent',
    'alignItems',
    'flexDirection',
    'flexWrap',
    'justifyContent',
    'justifyItems',
    'order',
    'gap',
    'columnGap',
    'rowGap',
    'gridColumn',
    'gridColumnStart',
    'gridColumnEnd',
    'gridRow',
    'gridRowStart',
    'gridRowEnd',
    'gridAutoFlow',
    'gridAutoColumns',
    'gridAutoRows',
    'gridTemplateColumns',
    'gridTemplateRows',
    'gridTemplateAreas',
    'gridArea',
    'height',
    'maxHeight',
    'maxWidth',
    'minHeight',
    'minWidth',
    'width',
    'boxSizing',
    'objectFit',
    'order',
    'overflow',
    'overflowX',
    'overflowY',
    'p',
    'padding',
    'pb',
    'paddingBottom',
    'pl',
    'paddingLeft',
    'pr',
    'paddingRight',
    'pt',
    'paddingTop',
    'px',
    'paddingX',
    'py',
    'paddingY',
    'pointerEvents',
    'textAlign',
    'textDecoration',
    'textDecorationLine',
    'textTransform',
    'textUnderlineOffset',
    'zIndex',
    'borderStyle',
    'borderTopStyle',
    'borderRightStyle',
    'borderBottomStyle',
    'borderLeftStyle',
    'borderWidth',
    'borderTopWidth',
    'borderRightWidth',
    'borderBottomWidth',
    'borderLeftWidth',
    'borderRadius',
    'whiteSpace',
    'overflowWrap',
    'opacity',
    'listStyleType',
    ...alignmentRelatedSystemProps,
];
/**
 * Extracts supported system props from an object
 *
 * @param props The props object
 *
 * @returns An `sx` object that can be passed on to a MUI component and a `nonSystemProps` object containing the non-system props
 */
export const extractSystemStyles = (props) => {
    const _props = { ...props };
    /**
     * First, we extract the internal system props manually.
     */
    const { color, backgroundColor, elevation, border, borderTop, borderRight, borderBottom, borderLeft, borderColor, borderTopColor, borderRightColor, borderBottomColor, borderLeftColor, ...rest } = _props;
    /**
     * Here we split the non-internal props into supported MUI system props and non-system props.
     */
    const nonSystemProps = {};
    const muiSystemProps = {};
    Object.keys(rest).forEach(propKey => {
        if (!isSupportedSystemProp(propKey)) {
            nonSystemProps[propKey] = rest[propKey];
            return;
        }
        if (rest[propKey] === null || rest[propKey] === undefined) {
            return;
        }
        muiSystemProps[propKey] = rest[propKey];
    });
    /**
     * We build the `sx` function from the extracted internal system props and supported MUI system props
     */
    const sx = (theme) => ({
        backgroundColor: ResponsiveValue.map(backgroundColor, getColor(theme)),
        color: ResponsiveValue.map(color, getColor(theme)),
        boxShadow: ResponsiveValue.map(elevation, getElevation(theme)),
        border: ResponsiveValue.map(border, colorKey => {
            return `1px solid ${getColor(theme)(colorKey)}`;
        }),
        borderTop: ResponsiveValue.map(borderTop, colorKey => {
            return `1px solid ${getColor(theme)(colorKey)}`;
        }),
        borderRight: ResponsiveValue.map(borderRight, colorKey => {
            return `1px solid ${getColor(theme)(colorKey)}`;
        }),
        borderBottom: ResponsiveValue.map(borderBottom, colorKey => {
            return `1px solid ${getColor(theme)(colorKey)}`;
        }),
        borderLeft: ResponsiveValue.map(borderLeft, colorKey => {
            return `1px solid ${getColor(theme)(colorKey)}`;
        }),
        borderColor: ResponsiveValue.map(borderColor, getColor(theme)),
        borderTopColor: ResponsiveValue.map(borderTopColor, getColor(theme)),
        borderRightColor: ResponsiveValue.map(borderRightColor, getColor(theme)),
        borderBottomColor: ResponsiveValue.map(borderBottomColor, getColor(theme)),
        borderLeftColor: ResponsiveValue.map(borderLeftColor, getColor(theme)),
        // We take advantage of the fact that MUI's system props are directly translatable to
        // the `sx` syntax.
        ...muiSystemProps,
    });
    /**
     * Lastly we return the `sx` function and the non system props.
     */
    return { sx, nonSystemProps };
};
const isSupportedSystemProp = (candidate) => {
    return supportedSystemProps.includes(candidate);
};
const getColor = (theme) => (color) => {
    return theme.modernFamlyTheme.colorPalette[color];
};
const getElevation = (theme) => (elevation) => {
    return theme.modernFamlyTheme.elevation[elevation];
};
export const handlePassthroughSx = (sx) => {
    return Array.isArray(sx) ? sx : [sx];
};
